// ── Journey-specific helpers for Platform Journey Tool ────────────────────────
import type { AccountRecord, AnyObj, SimAlert, SimTransaction, TmRuleConfig } from './types'
import { DEFAULT_TM_RULE_CONFIG } from './data'

// ── Account Setup — simulated Operations Center account IDs ──────────────────
// SIMULATED: there's no accessible Operations Center API/docs to call live yet
// (see stage copy), so this just mints a plausible-looking UUID client-side.

export function generateSimAccountId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function makeAccountRecord(accountType: 'parent' | 'child', name: string, version: 'v1' | 'v2'): AccountRecord {
  return {
    accountType,
    name,
    accountId: generateSimAccountId(),
    version,
    country: 'AU',
    portalUserName: '',
    portalUserEmail: '',
    notes: '',
  }
}

// ── Outcome derivation (mirrors testing-ground's KycV2Workspace outcomeLabel) ──

export function outcomeLabel(wr: AnyObj | null): string {
  if (!wr) return ''
  const s = (wr.workflowExecutionState ?? wr.overallRiskLevel ?? '').toUpperCase()
  if (wr.issues?.length || s === 'TIMEOUT' || s === 'ERROR') return 'FAIL'
  const rs = (wr.checkSummary?.overallRiskLevel ?? wr.riskAssessment?.riskLevel ?? s)
  if (rs === 'LOW' || rs === 'CLEAR' || rs === 'PASS') return 'PASS'
  if (rs === 'MEDIUM' || rs === 'REFER' || rs === 'REVIEW') return 'REFER'
  if (rs === 'HIGH' || rs === 'FAIL') return 'FAIL'
  return s || 'DONE'
}

// ── AML — pull the AML step out of the shared KYC workflow result ────────────

export function extractAmlStep(wr: AnyObj | null): AnyObj | null {
  if (!wr) return null
  const steps: AnyObj[] = wr.workflowStepResults ?? []
  return steps.find(s => (s.stepName ?? '').toUpperCase() === 'AML') ?? null
}

// ── Fraud — pull FRAUD_* process results out of the shared KYC workflow result ─

export function extractFraudChecks(wr: AnyObj | null): AnyObj[] {
  if (!wr) return []
  const steps: AnyObj[] = wr.workflowStepResults ?? []
  const fraudStep = steps.find(s => (s.stepName ?? '').toUpperCase() === 'FRAUD')
  const direct = (fraudStep?.processResults ?? []) as AnyObj[]
  if (direct.length > 0) return direct
  // Some workflows surface fraud checks as process results on other steps rather
  // than a dedicated FRAUD step — fall back to scanning all steps for FRAUD_* classes.
  return steps.flatMap(s => (s.processResults ?? []) as AnyObj[]).filter(p => String(p.class ?? '').startsWith('FRAUD_'))
}

// ── Duplicates — pull the duplicate check out of the shared KYC workflow result ─

export function extractDuplicateCheck(wr: AnyObj | null): AnyObj | null {
  if (!wr) return null
  const steps: AnyObj[] = wr.workflowStepResults ?? []
  const dupStep = steps.find(s => (s.stepName ?? '').toUpperCase() === 'DUPLICATE')
  const direct = (dupStep?.processResults ?? []) as AnyObj[]
  if (direct.length > 0) return direct[0]
  // Some workflows surface the duplicate check as a process result on another
  // step rather than a dedicated DUPLICATE step — fall back to scanning all steps.
  return steps.flatMap(s => (s.processResults ?? []) as AnyObj[]).find(p => String(p.class ?? p.objectType ?? '').toUpperCase().includes('DUPLICATE')) ?? null
}

/** Whether the shared KYC workflow result flagged a duplicate — null means no check ran yet. */
export function hasDuplicateMatch(check: AnyObj | null): boolean | null {
  if (!check) return null
  const result = String(check.result ?? '').toUpperCase()
  return result === 'MATCH' || result === 'FAIL' || result === 'REFER'
}

// ── Cascade waterfall — group each step's process results by provider/source ──

export interface CascadeSourceHit {
  stepName: string
  objectType: string
  result: string
  source: string
}

export function deriveCascadeWaterfall(wr: AnyObj | null): CascadeSourceHit[] {
  if (!wr) return []
  const steps: AnyObj[] = wr.workflowStepResults ?? []
  return steps.flatMap(step =>
    ((step.processResults ?? []) as AnyObj[]).map(pr => ({
      stepName: step.stepName ?? '—',
      objectType: pr.objectType ?? pr.class ?? '—',
      result: pr.result ?? '—',
      source: pr.providerResult?.source ?? pr.source ?? '—',
    })),
  )
}

// ── Transaction Monitoring simulation — simple, transparent rule engine ───────
// SIMULATED: FrankieOne has no Transaction Monitoring endpoint wired into this
// codebase yet, so this evaluates a small illustrative ruleset client-side
// rather than calling a live API.

const HOURS = 60 * 60 * 1000

export function runTmSimulation(transactions: SimTransaction[], config: TmRuleConfig = DEFAULT_TM_RULE_CONFIG): SimAlert[] {
  const alerts: SimAlert[] = []
  const sorted = [...transactions].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  const { reportingThreshold, structuringWindowHours, structuringMinCount, velocityWindowHours, velocityThreshold, flagNewPayee } = config

  for (const tx of sorted) {
    if (tx.direction === 'OUT' && tx.amount >= reportingThreshold) {
      alerts.push({
        id: `alert_threshold_${tx.id}`,
        transactionId: tx.id,
        rule: 'Reporting threshold',
        severity: 'HIGH',
        description: `Single transaction of ${tx.currency} ${tx.amount.toLocaleString()} exceeds the AUD ${reportingThreshold.toLocaleString()} reporting threshold`,
      })
    }
    if (flagNewPayee && tx.counterparty.toLowerCase().startsWith('new payee') && tx.amount >= reportingThreshold) {
      alerts.push({
        id: `alert_newpayee_${tx.id}`,
        transactionId: tx.id,
        rule: 'New payee, high value',
        severity: 'HIGH',
        description: `Large transfer to a newly added payee (${tx.counterparty})`,
      })
    }
  }

  // Structuring: repeated outbound transfers to the same counterparty, each just
  // under the reporting threshold, within the configured window.
  const byCounterparty = new Map<string, SimTransaction[]>()
  for (const tx of sorted) {
    if (tx.direction !== 'OUT' || tx.amount < reportingThreshold * 0.9 || tx.amount >= reportingThreshold) continue
    const list = byCounterparty.get(tx.counterparty) ?? []
    list.push(tx)
    byCounterparty.set(tx.counterparty, list)
  }
  for (const [counterparty, txs] of byCounterparty) {
    if (txs.length < structuringMinCount) continue
    const span = new Date(txs[txs.length - 1].timestamp).getTime() - new Date(txs[0].timestamp).getTime()
    if (span <= structuringWindowHours * HOURS) {
      for (const tx of txs) {
        alerts.push({
          id: `alert_structuring_${tx.id}`,
          transactionId: tx.id,
          rule: 'Structuring',
          severity: 'HIGH',
          description: `${txs.length} transfers to ${counterparty} just under the reporting threshold within ${structuringWindowHours} hours`,
        })
      }
    }
  }

  // Velocity: 3+ outbound transactions within the configured window exceeding the total threshold.
  for (let i = 0; i < sorted.length; i++) {
    const windowStart = new Date(sorted[i].timestamp).getTime()
    const windowTxs = sorted.filter(tx => {
      const t = new Date(tx.timestamp).getTime()
      return tx.direction === 'OUT' && t >= windowStart && t - windowStart <= velocityWindowHours * HOURS
    })
    const total = windowTxs.reduce((sum, tx) => sum + tx.amount, 0)
    if (windowTxs.length >= 3 && total > velocityThreshold) {
      const last = windowTxs[windowTxs.length - 1]
      const alertId = `alert_velocity_${last.id}`
      if (!alerts.some(a => a.id === alertId)) {
        alerts.push({
          id: alertId,
          transactionId: last.id,
          rule: 'Unusual velocity',
          severity: 'MEDIUM',
          description: `${windowTxs.length} outbound transactions totalling ${last.currency} ${total.toLocaleString()} within ${velocityWindowHours} hours`,
        })
      }
      break
    }
  }

  return alerts
}
